/**
 * Aspect — 24-hour day / night cycle
 * --------------------------------------------------------------
 * Rotates the photographic HDR skybox and moves the directional sun in real
 * time as the time-of-day slider changes. Shadows, reflections, and ambient
 * light all follow the clock.
 */

import * as pc from "playcanvas";

/** Default clock when a scene first loads (11:00). */
export const DEFAULT_INITIAL_HOUR = 11;

/** Yaw baked into the HDR so its sun aligns with hour 12 at setup. */
export const SKY_BASE_YAW = -24;

const clamp01 = (v) => Math.max(0, Math.min(1, v));

function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

const tmpX = new pc.Vec3();
const tmpZ = new pc.Vec3();
const sunRotMat = new pc.Mat4();
const sunRotQuat = new pc.Quat();
const sunDir = new pc.Vec3();
const sunColor = new pc.Color();
const warmColor = new pc.Color(1.0, 0.72, 0.45);
const zenithColor = new pc.Color(1.0, 0.98, 0.95);
const moonColor = new pc.Color(0.75, 0.85, 1.0);
const skyRot = new pc.Quat();

/** Format fractional hour as HH:MM (24h clock). */
export function formatTimeOfDay(hour) {
  const h = ((hour % 24) + 24) % 24;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60) % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** World-space direction the sunlight travels FROM (normalized). */
export function hourToSunDirection(hour, out = new pc.Vec3()) {
  const h = ((hour % 24) + 24) % 24;
  const orbit = (h / 24) * Math.PI * 2 - Math.PI / 2;
  const el = Math.sin(orbit) * 64 * (Math.PI / 180);
  const az = ((180 + (h - 12) * 15) % 360) * (Math.PI / 180);
  const cosEl = Math.cos(el);
  return out.set(cosEl * Math.sin(az), Math.sin(el), cosEl * Math.cos(az));
}

/** Sun elevation in degrees (− at night, + during day). */
export function hourToSunElevation(hour) {
  const h = ((hour % 24) + 24) % 24;
  const orbit = (h / 24) * Math.PI * 2 - Math.PI / 2;
  return Math.sin(orbit) * 64;
}

/** Orient a directional light so it shines from `fromWorld` into the scene. */
function orientLightFrom(entity, fromWorld) {
  const src = fromWorld;
  const ref = Math.abs(src.y) > 0.99 ? pc.Vec3.RIGHT : pc.Vec3.UP;
  tmpX.cross(src, ref).normalize();
  tmpZ.cross(tmpX, src).normalize();
  sunRotMat.set([
    tmpX.x, tmpX.y, tmpX.z, 0,
    src.x, src.y, src.z, 0,
    tmpZ.x, tmpZ.y, tmpZ.z, 0,
    0, 0, 0, 1,
  ]);
  sunRotQuat.setFromMat4(sunRotMat);
  entity.setRotation(sunRotQuat);
}

export class DayNightCycle {
  /**
   * @param {pc.AppBase} app
   * @param {{ lightingRoot: pc.Entity, camera: pc.Entity, initialHour?: number, skyBaseYaw?: number }} opts
   */
  constructor(app, { lightingRoot, camera, initialHour = DEFAULT_INITIAL_HOUR, skyBaseYaw = SKY_BASE_YAW } = {}) {
    this.app = app;
    this.camera = camera;
    this.skyBaseYaw = skyBaseYaw;
    this.sun = lightingRoot.findByName("sun");
    this.fill = lightingRoot.findByName("fill");
    this.topFill = lightingRoot.findByName("top-fill");
    this._baseSunIntensity = this.sun?.light?.intensity ?? 1.9;

    this._hour = initialHour;
    this._targetHour = initialHour;
    this._smoothSpeed = 18;

    this._onUpdate = (dt) => this._tick(dt);
    app.on("update", this._onUpdate);

    this.setHour(initialHour, true);
  }

  /** @param {number} hour 0–24 */
  setHour(hour, immediate = false) {
    this._targetHour = ((hour % 24) + 24) % 24;
    if (immediate) {
      this._hour = this._targetHour;
      this._apply();
      this.app.fire("time:changed", { hour: this._hour, label: formatTimeOfDay(this._hour) });
    }
  }

  get hour() {
    return this._hour;
  }

  _tick(dt) {
    let delta = this._targetHour - this._hour;
    if (delta > 12) delta -= 24;
    if (delta < -12) delta += 24;
    if (Math.abs(delta) < 0.001) return;

    const step = Math.sign(delta) * Math.min(Math.abs(delta), this._smoothSpeed * dt);
    this._hour = ((this._hour + step) % 24 + 24) % 24;
    this._apply();
    this.app.fire("time:changed", { hour: this._hour, label: formatTimeOfDay(this._hour) });
  }

  _apply() {
    const hour = this._hour;
    const elev = hourToSunElevation(hour);
    const day = smoothstep(-8, 20, elev);
    const twilight = Math.exp(-(elev * elev) / 30);
    const night = 1 - day;

    hourToSunDirection(hour, sunDir);

    // --- HDR skybox: spin + tilt so the photographed sun tracks the clock ---
    const azOffset = (hour - 12) * 15;
    const pitch = -elev * 0.55;
    skyRot.setFromEulerAngles(pitch, this.skyBaseYaw + azOffset, 0);
    if ("skyboxRotation" in this.app.scene) {
      this.app.scene.skyboxRotation = skyRot;
    }

    const scene = this.app.scene;
    scene.skyboxIntensity = 0.06 + day * 1.28 + twilight * 0.28;
    if ("skyboxHighlightMultiplier" in scene) {
      scene.skyboxHighlightMultiplier = 0.5 + day * 1.45 + twilight * 0.85;
    }
    if ("skyboxLuminance" in scene) {
      scene.skyboxLuminance = 1500 + day * 23000;
    }

    // Warm-biased daytime ambient fakes sun bounce off interior surfaces;
    // night keeps the cooler blue moon spill.
    scene.ambientLight = new pc.Color(
      0.06 + day * 0.44 + twilight * 0.06 + night * 0.04,
      0.07 + day * 0.4 + twilight * 0.03 + night * 0.05,
      0.14 + day * 0.3 + night * 0.12,
    );
    if ("ambientLuminance" in scene) {
      scene.ambientLuminance = 1200 + day * 23000;
    }

    // --- Directional sun: moves in real time, fades below horizon ---
    if (this.sun?.light) {
      orientLightFrom(this.sun, sunDir);

      const y = sunDir.y;
      const t = clamp01(y / 0.35);
      sunColor.lerp(warmColor, zenithColor, t);
      sunColor.lerp(sunColor, moonColor, night * 0.85);
      this.sun.light.color = sunColor;

      const sunPart = this._baseSunIntensity * smoothstep(-4, 12, elev);
      const moonPart = 0.35 * night;
      this.sun.light.intensity = sunPart + moonPart;
      this.sun.light.castShadows = elev > 2;
    }

    if (this.fill) this.fill.enabled = true;
    if (this.fill?.light) {
      this.fill.light.intensity = 0.05 + day * 0.5;
      this.fill.light.color = new pc.Color(
        0.55 + day * 0.15,
        0.62 + day * 0.16,
        0.75 + night * 0.15,
      );
    }

    if (this.topFill) this.topFill.enabled = true;
    if (this.topFill?.light) {
      this.topFill.light.intensity = 0.04 + day * 0.38 + night * 0.12;
      this.topFill.light.color = night > 0.5 ? moonColor : new pc.Color(0.88, 0.9, 0.98);
    }

    const cam = this.camera?.camera;
    if (cam) {
      cam.clearColor = new pc.Color(
        0.03 + day * 0.55 + twilight * 0.12 + night * 0.02,
        0.05 + day * 0.65 + twilight * 0.1 + night * 0.03,
        0.12 + day * 0.72 + night * 0.18,
      );
    }

    this.app.renderNextFrame = true;
  }

  destroy() {
    this.app.off("update", this._onUpdate);
  }
}
